import { sanitizeApiKey } from 'js/lib/URLUtils';
import { useExternalTriggerConfig } from './useExternalTriggerConfig';

interface Variable {
  param: string;
  default: any;
}

interface ConfigObject {
  id: string;
  data_credentials_id: string;
  variables?: Record<string, any>;
}

type PayloadType = 'combined' | 'global' | 'sources' | 'sinks';

const processVariables = (variables: Variable[] | undefined): Record<string, any> | undefined => {
  if (!variables || !Array.isArray(variables) || variables.length === 0) {
    return undefined;
  }

  return Object.fromEntries(
    variables
      .filter((variable) => variable.param && variable.default !== undefined)
      .map((variable) => [variable.param, variable.default]),
  );
};

const createConfigObject = (
  id: string | undefined,
  credentialsId: string | undefined,
  variables: Variable[] | undefined,
  includeVariables: boolean = true,
): ConfigObject => {
  const config: ConfigObject = {
    id: id ?? 'XXXX',
    data_credentials_id: credentialsId ?? 'XXXX',
  };

  if (includeVariables) {
    const processedVariables = processVariables(variables);
    if (processedVariables) {
      config.variables = processedVariables;
    }
  }

  return config;
};

const getPayload = (source: any, sinks: any[], type: PayloadType = 'combined') => {
  const {
    id: sourceId,
    data_credentials_id: sourceCredentialsId,
    run_variables: sourceVariables,
  } = source || {};

  const basePayload = {
    name: 'myTask',
  };

  // Process all variables from source and sinks
  const globalVariables: Record<string, any> = {};
  if (sourceVariables) {
    const processedSourceVars = processVariables(sourceVariables);
    if (processedSourceVars) {
      Object.assign(globalVariables, processedSourceVars);
    }
  }

  if (sinks?.length > 0) {
    sinks.forEach((sink) => {
      const { run_variables: sinkVariables } = sink || {};
      if (sinkVariables) {
        const processedSinkVars = processVariables(sinkVariables);
        if (processedSinkVars) {
          Object.assign(globalVariables, processedSinkVars);
        }
      }
    });
  }

  switch (type) {
    case 'global':
      return JSON.stringify(
        [
          {
            ...basePayload,
            global: {
              variables: Object.keys(globalVariables).length > 0 ? globalVariables : {},
            },
          },
        ],
        null,
        2,
      );

    case 'sources':
      return JSON.stringify(
        [
          {
            ...basePayload,
            sources: [createConfigObject(sourceId, sourceCredentialsId, sourceVariables)],
          },
        ],
        null,
        2,
      );

    case 'sinks':
      return JSON.stringify(
        [
          {
            ...basePayload,
            sinks: sinks.map((sink) => {
              const {
                id: sinkId,
                data_credentials_id: sinkCredentialsId,
                run_variables: sinkVariables,
              } = sink || {};
              return createConfigObject(sinkId, sinkCredentialsId, sinkVariables);
            }),
          },
        ],
        null,
        2,
      );

    case 'combined':
    default:
      const combinedPayload = { ...basePayload };

      if (Object.keys(globalVariables).length > 0) {
        Object.assign(combinedPayload, {
          global: { variables: globalVariables },
        });
      }

      Object.assign(combinedPayload, {
        sources: [createConfigObject(sourceId, sourceCredentialsId, sourceVariables, false)],
      });

      if (sinks?.length > 0) {
        Object.assign(combinedPayload, {
          sinks: sinks.map((sink) => {
            const {
              id: sinkId,
              data_credentials_id: sinkCredentialsId,
              run_variables: sinkVariables,
            } = sink || {};
            return createConfigObject(sinkId, sinkCredentialsId, sinkVariables, false);
          }),
        });
      }

      return JSON.stringify([combinedPayload], null, 2);
  }
};

const useExternalTriggerCurl = (
  flowNodeId: number,
  shouldSanitizeKey: boolean,
  payloadType: PayloadType = 'combined',
) => {
  const { runProfile, source, sinks } = useExternalTriggerConfig(flowNodeId);
  const { api_key, url } = runProfile || {};
  const runProfileApiKey = !api_key
    ? '[API KEY GENERATED ON SOURCE CREATION]'
    : api_key && shouldSanitizeKey
      ? sanitizeApiKey(api_key)
      : api_key;
  const runProfileUrl = url ? url : '[URL GENERATED ON SOURCE CREATION]';

  return `curl -X POST ${runProfileUrl} \\
  -H "Authorization: Basic ${runProfileApiKey}" \\
  -H "Content-Type: application/json" \\
  -d '${getPayload(source, sinks, payloadType)}'`;
};

export const getPayloadExamples = () => {
  return {
    combined: {
      title: 'Complete Override Example',
      description:
        'Override all flow settings in one call - global variables, source configurations, and destination settings',
      payloadType: 'combined' as PayloadType,
    },
    global: {
      title: 'Global Variables Only',
      description:
        'Override user-defined flow-wide variables and properties that apply to the entire pipeline execution',
      payloadType: 'global' as PayloadType,
    },
    sources: {
      title: 'Source Settings Only',
      description:
        'Override source credentials and configuration. See Nexla Docs for information on overriding partial or full destination configurations.',
      payloadType: 'sources' as PayloadType,
    },
    sinks: {
      title: 'Destination Settings Only',
      description:
        'Override destination credentials and configuration. See Nexla Docs for information on overriding partial or full source configurations.',
      payloadType: 'sinks' as PayloadType,
    },
  };
};

export default useExternalTriggerCurl;
